import React, { useState } from 'react';
import { Check, ChevronDown, Download, ExternalLink, Film, History } from 'lucide-react';
import type { ProjectGeneration } from '../../lib/canvasBoard';
import { downloadMedia, friendlyFileName } from '../../lib/downloadMedia';
import { getVideoModelById } from '../../lib/videoModelCatalog';

const COLLAPSED_KEY = 'seu.video.telaEmBranco.generationsCollapsed';

type Props = {
  generations: ProjectGeneration[];
  /** Video nodes generating or joining right now: their active version can't change. */
  busyVideoIds: Set<string>;
  projectTitle?: string;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  onActivate: (videoId: string, versionId: string) => void;
};

export function loadGenerationsCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function saveGenerationsCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    /* storage unavailable */
  }
}

function whenLabel(ts: number): string {
  if (!ts) return '';
  const diff = Math.max(0, Date.now() - ts);
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  return new Date(ts).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function modelLine(g: ProjectGeneration): string {
  const v = g.version;
  const model = v.videoModelId ? getVideoModelById(v.videoModelId)?.label : undefined;
  return [model, v.resolutionId].filter(Boolean).join(' · ');
}

function metaLine(g: ProjectGeneration): string {
  if (g.kind === 'join' && g.audioCount != null) return `vídeo + ${g.audioCount} ${g.audioCount === 1 ? 'faixa' : 'faixas'} de som`;
  if (g.kind === 'join') return `junta ${g.joinCount} vídeos na ordem`;
  const v = g.version;
  return [
    v.seconds ? `${v.seconds}s` : null,
    v.parts && v.parts > 1 ? `${v.parts} partes` : null,
    v.credits ? `${v.credits} cr` : null,
    whenLabel(v.createdAt) || null,
  ].filter(Boolean).join(' · ');
}

export default function CanvasGenerationsPanel(props: Props) {
  const { generations, busyVideoIds, collapsed } = props;
  const [previewId, setPreviewId] = useState<string | null>(null);

  if (!generations.length) return null;

  if (collapsed) {
    return (
      <button
        type="button"
        className="tb-tray-pill"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => props.onCollapsedChange(false)}
      >
        <History size={15} /> Gerações
        <span className="tb-proj-count">{generations.length}</span>
      </button>
    );
  }

  const preview = generations.find((g) => g.version.id === previewId);
  const flowCount = new Set(generations.filter((g) => g.kind === 'flow').map((g) => g.videoId)).size;
  const sectionCount = new Set(generations.map((g) => g.videoId)).size;

  return (
    <div className="tb-tray tb-proj tb-gen" onPointerDown={(e) => e.stopPropagation()}>
      <div className="tb-tray-head">
        <strong>Gerações</strong>
        <span className="tb-proj-count">{generations.length}</span>
        <button
          type="button"
          className="tb-tray-collapse tb-gen-collapse"
          aria-label="Recolher"
          title="Recolher"
          onClick={() => props.onCollapsedChange(true)}
        >
          <ChevronDown size={15} />
        </button>
      </div>
      <div className="tb-tray-hint">
        {flowCount > 1 ? `${flowCount} fluxos · ` : ''}clique para assistir · “Usar” troca o vídeo do fluxo
      </div>

      {preview && (
        <div className="tb-gen-preview">
          <video key={preview.version.id} src={preview.version.clipUrl} controls controlsList="nodownload" autoPlay playsInline />
        </div>
      )}

      <div className="tb-proj-list tb-gen-list" role="list">
        {generations.map((g, i) => {
          const showFlow = sectionCount > 1 && g.kind === 'flow' && (i === 0 || generations[i - 1].videoId !== g.videoId);
          const busy = busyVideoIds.has(g.videoId);
          return (
            <React.Fragment key={g.version.id}>
              {showFlow && <div className="tb-gen-flow">{g.flowLabel}</div>}
              <div
                role="listitem"
                className={`tb-proj-row${g.active ? ' is-active' : ''}${previewId === g.version.id ? ' is-preview' : ''}`}
              >
                <button
                  type="button"
                  className="tb-proj-open"
                  onClick={() => setPreviewId((cur) => (cur === g.version.id ? null : g.version.id))}
                  title={previewId === g.version.id ? 'Fechar prévia' : 'Assistir'}
                >
                  <span className="tb-proj-thumb">
                    <video src={`${g.version.clipUrl}#t=0.5`} muted playsInline preload="metadata" />
                    <span className="tb-tray-badge"><Film size={10} /></span>
                  </span>
                  <span className="tb-proj-text">
                    <span className="tb-proj-title">
                      {g.kind === 'join' ? g.flowLabel : `v${g.versionNumber}`}
                      {g.kind === 'flow' && modelLine(g) ? <span className="tb-gen-model"> · {modelLine(g)}</span> : null}
                    </span>
                    <span className="tb-proj-meta">
                      {g.active && g.kind === 'flow' && <span className="tb-proj-badge">Ativa</span>}
                      {metaLine(g) || (sectionCount > 1 ? '' : g.flowLabel)}
                    </span>
                  </span>
                </button>
                <span className="tb-gen-actions">
                  {g.kind === 'join' ? null : g.active ? (
                    <span className="tb-gen-active" title="Versão que o fluxo toca e junta"><Check size={13} /></span>
                  ) : (
                    <button
                      type="button"
                      className="tb-gen-use"
                      disabled={busy}
                      title={busy ? 'Aguarde a geração terminar' : 'Usar esta versão no fluxo'}
                      onClick={() => props.onActivate(g.videoId, g.version.id)}
                    >
                      Usar
                    </button>
                  )}
                  <button
                    type="button"
                    className="tb-gen-open"
                    aria-label={`Baixar v${g.versionNumber}`}
                    title="Baixar vídeo"
                    onClick={() => void downloadMedia(g.version.clipUrl, friendlyFileName({
                      project: props.projectTitle,
                      reference: g.kind === 'join' ? g.flowLabel : `${g.flowLabel || 'video'}-v${g.versionNumber}`,
                      url: g.version.clipUrl,
                      at: g.version.createdAt,
                    }))}
                  >
                    <Download size={12} />
                  </button>
                  <a
                    className="tb-gen-open"
                    href={g.version.clipUrl}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Abrir v${g.versionNumber} em nova aba`}
                    title="Abrir em nova aba"
                  >
                    <ExternalLink size={12} />
                  </a>
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
